import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MotionConfig } from "motion/react";
import { CspNonceProvider } from "@brevalt/ui";
import copy from "virtual:brevalt-i18n/en";
import { configureApiClient, isMfaRequired } from "@brevalt/api-client";
import {
  applyCspNonce,
  browserLanguages,
  createI18n,
  readStoredLocale,
  resolveLocale,
  SOURCE_LOCALE,
  takeLocaleParam,
  workspace,
} from "@brevalt/app-core";
import { LOCALES, localeRuntime } from "@brevalt/app-core/locale-runtime";
import { App } from "./app";
import { loadRestCopy } from "./copy-rest";
import { session } from "./auth";
import { config } from "./config";
import { captureExportToken } from "./shared/export-link";
import "./styles.css";

// The workspace (X-Brevalt-Tenant) comes from the switcher (F2) through the store; a switch resets the cache.
configureApiClient("customer", {
  baseUrl: config.apiBaseUrl,
  getAccessToken: session.getAccessToken,
  getTenantId: workspace.get,
  // A password-level token (right after sign-up or a reset): sign in again with the second factor, no error
  // (HANDOFF §0.3). One redirect per page however many calls answer 401 (the session ignores the others).
  onUnauthorized: (error) => {
    if (!isMfaRequired(error)) return;
    session.requireSecondFactor(window.location.pathname + window.location.search).catch((e: unknown) => {
      console.error("sign-in with the second factor did not start", e);
    });
  },
});

// The locale (L1): this browser's stored choice, else its languages; the profile's, once `/me` is read (app.tsx).
// The public site's `/login` and `/signup` name the language it was read in (`?lang=`, QA F-L1e): stored as the
// choice first, so the sign-in's `ui_locales` gives Keycloak's pages that language. Before the sign-in reads the URL.
// English's first catalogue is in the entry; another locale's is loaded before the first render.
takeLocaleParam(LOCALES);
const startLocale = resolveLocale(LOCALES, { stored: readStoredLocale(), browser: browserLanguages() });

// The rest of the copy, in parallel with the sign-in (src/copy-rest.tsx); back from the sign-in also the dashboard's
// chunk (where most sign-ins land), so neither waits for the other once the sign-in is done (F14 perf, K17). Only
// when the bundle came from the browser's cache: on a cold load the network is still busy with it.
void loadRestCopy(startLocale);
if (window.location.pathname === "/callback" && bundleFromCache()) void import("./dashboard/home");

function bundleFromCache(): boolean {
  const own = performance
    .getEntriesByType("resource")
    .filter((e): e is PerformanceResourceTiming => /\/assets\/index-[^/]+\.js$/.test(e.name));
  return own.length > 0 && own.every((e) => e.transferSize === 0);
}

// An export link's one-time token leaves the address bar before anything (sign-in included) reads it.
captureExportToken();

// This response's CSP nonce for the styles the app creates (a dialog's scroll lock; Motion's pop layout; the kit's
// Select through CspNonceProvider, #102 a), before
// anything renders.
const nonce = applyCspNonce(import.meta.env.BREVALT_CSP_NONCE_PLACEHOLDER) ?? undefined;

async function i18nFor(locale: string) {
  const i18n = createI18n(copy);
  if (locale !== SOURCE_LOCALE) {
    // Not stored: only an explicit choice (Settings, the profile) is remembered.
    await localeRuntime.switchTo(i18n, locale, { remember: false }).catch((e: unknown) => {
      console.error(`i18n: ${locale} did not load, English is shown`, e);
    });
  }
  return i18n;
}

const root = document.getElementById("root");
if (root) {
  const i18n = await i18nFor(startLocale);
  createRoot(root).render(
    <StrictMode>
      <CspNonceProvider nonce={nonce}>
        <MotionConfig nonce={nonce}>
          <App i18n={i18n} session={session} />
        </MotionConfig>
      </CspNonceProvider>
    </StrictMode>,
  );
}
